class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.summary = this.mainDetailsToggle.querySelector('summary');
    this.content = this.summary.nextElementSibling;

    this.mainDetailsToggle.addEventListener(
      'focusout',
      this.onFocusOut.bind(this),
    );
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle
      .querySelector('summary')
      .setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class DetailsDropdown extends DetailsDisclosure {
  constructor() {
    super();

    // TODO: Test fallback current el
    this.current =
      this.summary.querySelector('[data-current]') ??
      this.summary.querySelector('span:not(.visually-hidden)') ??
      this.summary;
    this.list = this.querySelector('[role="listbox"]');
    this.options = [...this.content.querySelectorAll('[role="option"]')];
    this.callbacks = {
      init: [],
      destroy: [],
      change: {},
    };
    this.defaultState = {
      focusIndex: null,
      search: {
        input: '',
        matches: [],
        index: null,
      },
    };
    this.state = { ...this.defaultState };
  }

  init() {
    // this.createAnnouncementContainer();
    this.list.addEventListener('keydown', this.onInput.bind(this));
    this.options.forEach((option) => {
      if (option.getAttribute('aria-selected') === 'true') {
        option.setAttribute('tabindex', '-1');
      } else {
        option.setAttribute('tabindex', '0');
      }
      option.addEventListener('click', this.onOptionClick.bind(this));
    });

    this.runCallbacks('init', this.selected);
  }

  destroy() {
    this.list.removeEventListener('keydown', this.onInput.bind(this));
    this.options.forEach((option) =>
      option.removeEventListener('click', this.onOptionClick.bind(this)),
    );
    this.runCallbacks('destroy', this.selected);
  }

  createAnnouncementContainer() {
    const announcementContainer = document.createElement('div');
    announcementContainer.setAttribute('aria-live', 'off');
    // announcementContainer.setAttribute('role', 'alert');
    announcementContainer.style.display = 'block';
    announcementContainer.classList.add('visually-hidden');
    this.append(announcementContainer);
    this.announcementContainer = announcementContainer;
  }

  announce(announcement) {
    if (!announcement || announcement.trim() === '') return;
    this.announcementContainer.textContent = announcement;
    this.announcementContainer.setAttribute('aria-live', 'assertive');
    if (this.announcementContainer.getAttribute('role') != 'alert') {
      this.announcementContainer.setAttribute('role', 'alert');
    }
    setTimeout(() => {
      this.announcementContainer.textContent = '';
      this.announcementContainer.setAttribute('aria-live', 'off');
    }, 1000);
  }

  onToggle() {
    super.onToggle();

    if (this.mainDetailsToggle.open) {
      setTimeout(() => {
        // const activeLabel = this.getOptionLabel(this.selectedElement)
        this.list.focus();
        // this.announce(`Current selection: ${activeLabel}`);
      });
    }
  }

  onFocusOut(e) {
    setTimeout(() => {
      if (
        !this.contains(document.activeElement) &&
        !this.contains(e.relatedTarget)
      )
        this.close();
    });
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.destroy();
  }

  get selected() {
    return this.current.textContent.trim();
  }

  set selected(value) {
    this.onChange(value);
  }

  get selectedElement() {
    return this.getOptionElement();
  }

  set selectedElement(element) {
    this.onChange(this.getOptionValue(element), this.getOptionLabel(element));
  }

  get selectedIndex() {
    return this.options.indexOf(this.selectedElement);
  }

  set selectedIndex(index) {
    this.selectedElement = this.options[index];
  }

  get activeOptions() {
    return this.options.filter(
      (option) => option.getAttribute('aria-selected') !== 'true',
    );
  }

  setSelected(value, announce = true) {
    this.onChange(value, null, announce);
  }

  getOptionValue(option) {
    if (typeof option === 'string') return option;
    return option.getAttribute('data-value') ?? option.textContent.trim();
  }

  getOptionLabel(option) {
    if (typeof option === 'string')
      option = this.options.find((opt) => this.getOptionValue(opt) === option);
    return option.getAttribute('aria-label') ?? this.getOptionValue(option);
  }

  getOptionElement(v) {
    v ??= this.selected;
    return this.options.find((option) => {
      const { value, label } = this.getOptionData(option);
      return value === v || label === v;
    });
  }

  getOptionData(option) {
    return {
      value: this.getOptionValue(option),
      label: this.getOptionLabel(option),
    };
  }

  onOptionClick(event) {
    event.preventDefault();
    const selected = event.currentTarget;
    const { value, label } = this.getOptionData(selected);

    this.onChange(value, label, true);
  }

  onChange(value, label, announce = false) {
    label ??= this.getOptionLabel(value);
    this.options.forEach((option) => {
      if (this.getOptionValue(option) === value) {
        option.setAttribute('aria-selected', true);
        option.setAttribute('tabindex', '-1');
      } else {
        option.setAttribute('aria-selected', false);
        option.setAttribute('tabindex', '0');
      }
    });

    this.runChangeCallbacks(value);
    this.current.textContent = value;
    this.current.setAttribute('aria-label', label);
    // this.announceChange(value, label)
    if (this.mainDetailsToggle.open) {
      this.close(true);
    }
    this.clearSearch();
    // if (announce) {
    //   this.announce(`Selected: ${label}`);
    // }
  }

  clearSearch() {
    this.state.search = { ...this.defaultState.search };
  }

  clearFocusIndex() {
    this.state.focusIndex = null;
  }

  clearState() {
    this.state = { ...this.defaultState };
  }

  onInput(e) {
    if (!this.mainDetailsToggle.open) return;

    e.preventDefault();
    const { key } = e;

    if (this.mainDetailsToggle.open) {
      switch (key) {
        case 'Escape':
          this.close(true);
          break;
        case 'ArrowDown':
          this.moveFocus(1);
          break;
        case 'ArrowUp':
          this.moveFocus(-1);
          break;
        case 'Enter':
        case ' ':
          this.selectFocusedOption();
          break;
        default:
          // Handle alphanumeric key presses for mini-search
          this.onSearchInput(key);
          break;
      }
    }
  }

  moveFocus(direction = 1) {
    const directionMap = {
      down: 1,
      up: -1,
    };
    if (typeof direction === 'string') direction = directionMap[direction];

    const previousIndex = this.state.focusIndex;
    if (direction > 0) {
      this.state.focusIndex =
        this.state.focusIndex === null ? 0 : this.state.focusIndex + 1;
    } else if (direction < 0) {
      this.state.focusIndex =
        this.state.focusIndex === null
          ? this.activeOptions.length - 1
          : this.state.focusIndex - 1;
    }

    if (this.state.focusIndex >= this.activeOptions.length) {
      this.state.focusIndex = 0;
    } else if (this.state.focusIndex < 0) {
      this.state.focusIndex = this.activeOptions.length - 1;
    }

    if (this.state.focusIndex === previousIndex) return;

    this.applyFocus();
  }

  applyFocus(index = this.state.focusIndex) {
    this.state.focusIndex = index;
    const option = this.activeOptions[index];

    option.focus();

    option.scrollIntoView({
      block: 'nearest',
    });

    // this.announce(`Selecting option: ${this.getOptionLabel(option)}`);
  }

  selectFocusedOption() {
    this.selectedElement = this.activeOptions[this.state.focusIndex];
  }

  onSearchInput(key) {
    // Match options based on label or value
    const getMatch = (input) =>
      this.activeOptions.filter((option) => {
        const { value, label } = this.getOptionData(option);

        return (
          value.toLowerCase().startsWith(input) ||
          label.toLowerCase().startsWith(input)
        );
      });

    const char = key.toLowerCase();

    const input = this.state.search.input + char;

    let matchingOptions = getMatch(input);

    // If single character is repeated without a change in matches, focus next match
    // Otherwise, focus on first match from new input
    if (!matchingOptions.length && char === this.state.search.input) {
      matchingOptions = this.state.search.matches;
      this.state.search.index += 1;
    } else {
      this.state.search.input = input;
      this.state.search.index = 0;
    }

    if (matchingOptions.length) {
      this.state.search.matches = matchingOptions;

      if (this.state.search.index === matchingOptions.length) {
        this.state.search.index = 0;
      }
      let value = matchingOptions[this.state.search.index];
      const index = this.activeOptions.indexOf(value);
      this.applyFocus(index);
    } else {
      const previousMatches = this.state.search.matches;
      // Clear search state if no matches found
      this.clearSearch();

      // If previous input had matches, run search again with new input character
      if (previousMatches.length) this.onSearchInput(key);
    }
  }

  addCallback(type, callback, ...args) {
    switch (type) {
      case 'change':
        const [value = 'all', ...callbackArgs] = args;
        this.callbacks[type][value] ??= [];
        this.callbacks[type][value].push([callback, ...callbackArgs]);
        break;
      default:
        this.callbacks[type].push([callback, ...args]);
        break;
    }
  }

  runCallback(callbackData, ...args) {
    const [callback, ...additionalArgs] = callbackData;
    callback(...args, ...additionalArgs);
  }

  runCallbacks(type, ...args) {
    this.callbacks[type]?.forEach((callback) =>
      this.runCallback(callback, ...args),
    );
  }

  // Change callback args: currentValue, previousValue, currentOptionElement, previousOptionElement
  runChangeCallbacks(value) {
    this.callbacks.change.all?.forEach((callback) =>
      this.runCallback(
        callback,
        value,
        this.selected,
        this.getOptionElement(value),
        this.selectedElement,
      ),
    );

    this.callbacks.change[value]?.forEach((callback) =>
      this.runCallback(
        callback,
        value,
        this.selected,
        this.getOptionElement(value),
        this.selectedElement,
      ),
    );
  }

  close(refocus = false) {
    super.close();
    this.clearState();
    if (refocus) {
      this.summary.focus();
    }
  }
}

customElements.define('details-dropdown', DetailsDropdown);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define('header-menu', HeaderMenu);

class DetailsDisclosureBase extends HTMLElement {
  constructor(focusOut = true) {
    super();
    this.name = this.dataset.name ?? 'disclosure';
    this.containerClasses = [`has-open-${this.name}`];
    this.focusOut = focusOut;
    this.onSummaryClick = this.handleSummaryClick.bind(this);
    this.onFocusOut = this.handleFocusOut.bind(this);
    this.onKeyUp = this.handleKeyup.bind(this);

    this.disclosureContainer = this.closest('[data-disclosure-container]');
    this.detailsElement = this.querySelector('details');
    this.summaryElement = this.querySelector('summary');
  }

  connectedCallback() {
    this.bindEvents();
  }

  disconnectedCallback() {
    this.unbindEvents();
  }

  bindEvents() {
    this.summaryElement.addEventListener('click', this.onSummaryClick);

    if (this.focusOut) {
      this.detailsElement.addEventListener('focusout', this.onFocusOut);
    }

    this.detailsElement.addEventListener('keyup', this.onKeyUp);
  }

  unbindEvents() {
    this.summaryElement.removeEventListener('click', this.onSummaryClick);

    if (this.focusOut) {
      this.detailsElement.removeEventListener('focusout', this.onFocusOut);
    }

    this.detailsElement.removeEventListener('keyup', this.onKeyUp);
  }

  isOpen() {
    return this.detailsElement.hasAttribute('open');
  }

  handleSummaryClick(event) {
    event.preventDefault();
    this.isOpen() ? this.close() : this.open();
  }

  handleFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  handleKeyup(event) {
    event.code.toUpperCase() === 'ESCAPE' && this.close();
  }

  open() {
    this.detailsElement.setAttribute('open', true);
    this.disclosureContainer.classList.add(...this.containerClasses);
  }

  close() {
    this.detailsElement.removeAttribute('open');
    this.summaryElement.setAttribute('aria-expanded', false);
    this.disclosureContainer.classList.remove(...this.containerClasses);
  }
}

class MenuDisclosure extends DetailsDisclosureBase {
  constructor() {
    super();

    this.item = this.closest('li');
    this.child = this.querySelector('summary + div');
    this.siblings = Array.from(
      this.disclosureContainer.querySelectorAll('menu-disclosure')
    ).filter(sibling => sibling !== this);
    this.allChildren = Array.from(
      this.disclosureContainer.querySelectorAll('menu-disclosure summary + div')
    );
    this.background = this.disclosureContainer.querySelector(
      '[data-menu-background]'
    );

    this.header = document.querySelector('.section-header');
    this.headerWrapper = document.querySelector('.header-wrapper');
    this.rootLinks = this.disclosureContainer.querySelectorAll(
      '.header__menu-item--link, .header__icon'
    );

    this.timeline = gsap.timeline({ paused: true });
    this.openTimeline = gsap.timeline({ paused: true });
    this.switchTimeline = gsap.timeline({
      paused: true,
      onComplete: () =>
        this.disclosureContainer.classList.remove('is-switching'),
    });
    this.onMouseOver = this.handleMouseOver.bind(this);
    this.onClose = this.handleClose.bind(this);
    this.onRootLinkHover = this.handleRootLinkHover.bind(this);
    this.hoverIntentListeners = null;

    this.getHeaderHeight();
    this.buildTimelines();
  }

  bindEvents() {
    super.bindEvents();

    if (typeof window.hoverintent !== 'undefined') {
      const main = hoverintent(this, this.onMouseOver, () => { });
      main.options({ interval: 50 });

      const rootLinks = [];
      this.rootLinks.forEach(link => {
        const rootLinkListener = hoverintent(
          link,
          this.onRootLinkHover,
          () => { }
        );
        rootLinkListener.options({ interval: 50 });
        rootLinks.push(rootLinkListener);
      });

      this.hoverIntentListeners = { main, rootLinks };
    } else {
      this.addEventListener('mouseenter', this.onMouseOver);
      this.rootLinks.forEach(link =>
        link.addEventListener('mouseenter', this.onRootLinkHover)
      );
    }

    this.detailsElement.addEventListener('focusin', this.onMouseOver);
    this.detailsElement.addEventListener('focusout', this.onClose);
    this.headerWrapper.addEventListener('mouseleave', this.onClose);
    this.isSwitching = false;
  }

  unbindEvents() {
    super.unbindEvents();

    if (this.hoverIntentListeners) {
      this.hoverIntentListeners.main.remove();
      this.hoverIntentListeners.rootLinks.forEach(listener =>
        listener.remove()
      );
      this.hoverIntentListeners = null;
    } else {
      this.removeEventListener('mouseenter', this.onMouseOver);
      this.rootLinks.forEach(link =>
        link.removeEventListener('mouseenter', this.onRootLinkHover)
      );
    }

    this.detailsElement.removeEventListener('focusin', this.onMouseOver);
    this.detailsElement.removeEventListener('focusout', this.onClose);
    this.headerWrapper.removeEventListener('mouseleave', this.onClose);
  }

  handleSummaryClick() { }

  getSubnavHeight() {
    return this.child.offsetHeight;
  }

  buildTimeline(timeline, switching) {
    timeline
      .to(this.background, {
        y: this.getSubnavHeight.bind(this),
        duration: switching ? 0.3 : 0.4,
        ease: 'power1.inOut',
      })
      .to(
        this.allChildren,
        {
          opacity: 0,
          duration: 0.3,
          ease: 'power1.out',
        },
        0
      )
      .fromTo(
        this.child,
        {
          opacity: 0,
        },
        {
          opacity: 1,
          duration: switching ? 0.4 : 0.3,
          ease: switching ? 'power1.in' : 'power1.inOut',
        },
        switching ? 0 : 0.2
      );
  }

  hideSiblings() {
    this.allChildren
      .filter(child => child !== this.child)
      .forEach(child => gsap.set(child, { opacity: 0 }));
  }

  buildTimelines() {
    this.buildTimeline(this.openTimeline, false);
    this.buildTimeline(this.switchTimeline, true);
  }

  isOpen() {
    return this.item.classList.contains('is-open');
  }

  handleMouseOver() {
    this.open();
  }

  handleClose() {
    this.cancelClose();

    this.closingTime = setTimeout(() => {
      this.close();
    }, 150);
  }

  handleRootLinkHover() {
    if (this.isOpen()) {
      this.handleClose();
    }
  }

  getHeaderHeight() {
    if (
      document.documentElement.style.getPropertyValue(
        '--header-bottom-position-desktop'
      ) === ''
    ) {
      document.documentElement.style.setProperty(
        '--header-bottom-position-desktop',
        `${Math.floor(this.headerWrapper.getBoundingClientRect().bottom)}px`
      );
      document.documentElement.style.setProperty(
        '--header-height',
        `${Math.floor(this.headerWrapper.offsetHeight)}px`
      );
    }
  }

  open() {
    this.getHeaderHeight();

    this.isSwitching = this.hasOpenSibling();

    this.cancelClose();
    this.closeSiblings();

    if (this.isOpen()) return;

    super.open();

    if (this.isSwitching) {
      this.disclosureContainer.classList.add('is-switching');
    }

    this.item.classList.add('is-open');
    this.headerWrapper.preventHide = true;

    setTimeout(() => {
      this.disclosureContainer.classList.add('has-menu-buffer');

      if (this.isSwitching) {
        this.switchTimeline.invalidate().play(0);
      } else {
        this.openTimeline.invalidate().play(0);
      }
    });
  }

  hasOpenSibling() {
    return this.siblings.some(sibling => sibling.isOpen());
  }

  cancelClose() {
    clearTimeout(this.closingTime);
    this.closingTime = undefined;
  }

  closeSiblings() {
    this.siblings.forEach(sibling => sibling.close());
  }

  close() {
    this.cancelClose();

    if (!this.isOpen()) return;

    this.headerWrapper.preventHide = false;
    this.item.classList.remove('is-open');
    this.summaryElement.setAttribute('aria-expanded', false);

    setTimeout(() => {
      if (!this.hasOpenSibling()) {
        this.disclosureContainer.classList.remove(...this.containerClasses);
        this.timeline.pause();
        gsap.to(this.child, {
          opacity: 0,
          duration: 0.3,
          y: -32,
          ease: 'power1.inOut',
        });

        gsap.to(this.background, {
          y: 0,
          duration: 0.5,
          ease: 'power1.inOut',
          delay: 0.1,
          onComplete: () => gsap.set(this.child, { y: 0 }),
        });
      }
    });

    this.closeAnimation();
  }

  closeAnimation() {
    let animationStart;

    const handleAnimation = time => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else if (!this.isOpen()) {
        this.detailsElement.removeAttribute('open');

        if (!this.hasOpenSibling()) {
          this.disclosureContainer.classList.remove('has-menu-buffer');
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-disclosure', MenuDisclosure);
